import { Outlet, Link, Navigate, useLocation, useParams } from 'react-router';
import { ChevronRight, ExternalLink, Settings, FileText, BarChart3, Calendar, Grid3x3, ListTree, History, Layers, ClipboardList, Megaphone, Newspaper, Users, CalendarDays, Search, Home, UserCircle2, DollarSign, Archive, ChevronDown, LayoutGrid, Clock } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Project } from '../types';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { DiligentSlimBanner } from './DiligentSlimBanner';
import { ProfileCompletionPrompt } from './ProfileCompletionPrompt';
import { SystemSearch } from './SystemSearch';
import { useApp } from '../providers/AppProvider';
import { hasAcceptedProjectNda } from '../utils/projectNda';
import { canManageProjectSettings, canViewProjectAccountingUi, getProjectMemberRole, isExternalPartnerUser } from '../utils/projectAccess';
import { getTerm } from '../utils/terminology';
import { ProjectNdaGate } from './ProjectNdaGate';
import { getOrgFromUrl, normalizeOrganizationId } from '../utils/urlToOrg';
import { parseDateLike } from '../utils/dateUtils';

type ProjectNavGroupId = 'top' | 'planning' | 'finances' | 'archives';

const NAV_GROUP_DEFS: { id: Exclude<ProjectNavGroupId, 'top'>; label: string; icon: React.ComponentType<{ className?: string }> }[] = [
  { id: 'planning', label: 'Planning', icon: LayoutGrid },
  { id: 'finances', label: 'Finances', icon: DollarSign },
  { id: 'archives', label: 'References', icon: Archive },
];

type ProjectNavigationItem = {
  name: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  group: ProjectNavGroupId;
  match?: (pathname: string) => boolean;
};

export default function ProjectLayout() {
  const { state } = useApp();
  const location = useLocation();
  const { projectId } = useParams<{ projectId: string }>();
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [openNavGroup, setOpenNavGroup] = useState<string | null>(null);
  const navGroupRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (navGroupRef.current && !navGroupRef.current.contains(event.target as Node)) {
        setOpenNavGroup(null);
      }
    };

    if (openNavGroup) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [openNavGroup]);
  const urlOrganizationId = useMemo(() => normalizeOrganizationId(getOrgFromUrl()), []);

  const project = useMemo<Project | null>(() => {
    const projects = state?.projects ?? [];
    return projects.find(p => String(p.id).toLowerCase() === String(projectId).toLowerCase()) || null;
  }, [state?.projects, projectId]);

  const organizationContext = useMemo(() => {
    if (!project) {
      return { name: '', isUrlDifferent: false };
    }

    const organizations = state?.organizations ?? [];
    const primaryProjectOrgId = normalizeOrganizationId(project.organizationId) || project.organizationId;

    const primaryProjectOrg = organizations.find(
      (org) => normalizeOrganizationId(org.id) === primaryProjectOrgId
    );

    return {
      name: primaryProjectOrg?.name || project.organizationId || 'Unknown Organization',
      isUrlDifferent:
        Boolean(urlOrganizationId) &&
        Boolean(primaryProjectOrgId) &&
        urlOrganizationId !== primaryProjectOrgId,
    };
  }, [project, state?.organizations, urlOrganizationId]);

  const organizationName = organizationContext.name;

  const currentUser = state?.currentUser ?? null;
  const projectMemberRole = useMemo(
    () => getProjectMemberRole(project, currentUser?.id ?? '', currentUser?.email),
    [project, currentUser]
  );
  const ndaAccepted = useMemo(
    () => hasAcceptedProjectNda(project, currentUser, projectMemberRole),
    [project, currentUser, projectMemberRole]
  );
  const canOpenSettings = useMemo(
    () => canManageProjectSettings(currentUser, project),
    [currentUser, project]
  );
  const displayRole = useMemo(() => {
    const role = (projectMemberRole || currentUser?.role || 'member').replace(/_/g, ' ');
    return role
      .split(' ')
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  }, [projectMemberRole, currentUser?.role]);

  const isExternalPartner = useMemo(
    () => isExternalPartnerUser(currentUser, project),
    [currentUser, project]
  );

  const overviewPath = `/app/projects/${projectId}`;
  const newsFeedPath = `/app/projects/${projectId}/newsfeed`;
  const tasksPath = `/app/projects/${projectId}/tasks`;
  const epicsPath = `/app/projects/${projectId}/epics`;
  const requirementsPath = `/app/projects/${projectId}/requirements`;
  const spreadsheetPath = `/app/projects/${projectId}/spreadsheet`;
  const costsPath = `/app/projects/${projectId}/costs`;
  const planningBoardPath = `/app/projects/${projectId}/planning`;
  const sprintsPath = `/app/projects/${projectId}/sprints`;
  const sprintPlanningPath = `/app/projects/${projectId}/sprint-planning`;
  const ganttPath = `/app/projects/${projectId}/gantt`;
  const calendarPath = `/app/projects/${projectId}/calendar`;
  const standupPath = `/app/projects/${projectId}/standup`;
  const meetingsPath = `/app/projects/${projectId}/meetings`;
  const changeLogsPath = `/app/projects/${projectId}/changelogs`;
  const settingsPath = `/app/projects/${projectId}/settings`;
  const accountingPath = `/app/projects/${projectId}/accounting`;

  const fullProjectNavigation = useMemo<ProjectNavigationItem[]>(() => {
    const items: ProjectNavigationItem[] = [
      {
        name: 'Overview',
        href: overviewPath,
        icon: FileText,
        group: 'top',
      },
      {
        name: 'Assigned Tasks',
        href: tasksPath,
        icon: ListTree,
        group: 'top',
        match: (pathname) => pathname === tasksPath || pathname.startsWith(`/app/projects/${projectId}/task/`),
      },
      {
        name: 'Spreadsheet',
        href: spreadsheetPath,
        icon: Grid3x3,
        group: 'top',
      },
      {
        name: 'Costs',
        href: costsPath,
        icon: DollarSign,
        group: 'planning',
      },
      {
        name: 'Calendar',
        href: calendarPath,
        icon: CalendarDays,
        group: 'top',
      },
      {
        name: 'Standup',
        href: standupPath,
        icon: Megaphone,
        group: 'top',
      },
      {
        name: 'Meetings',
        href: meetingsPath,
        icon: Users,
        group: 'top',
      },
      {
        name: 'Planning Board',
        href: planningBoardPath,
        icon: BarChart3,
        group: 'planning',
      },
      {
        name: getTerm('workflow', project?.projectType),
        href: sprintsPath,
        icon: BarChart3,
        group: 'planning',
        match: (pathname) => pathname === sprintsPath || pathname.startsWith(`${sprintsPath}/`),
      },
      {
        name: 'Sprint Planning',
        href: sprintPlanningPath,
        icon: Calendar,
        group: 'planning',
      },
      {
        name: 'Gantt',
        href: ganttPath,
        icon: Calendar,
        group: 'planning',
      },
      {
        name: 'Feeds',
        href: newsFeedPath,
        icon: Newspaper,
        group: 'archives',
      },
      {
        name: getTerm('largeGoal', project?.projectType),
        href: epicsPath,
        icon: Layers,
        group: 'archives',
      },
      {
        name: 'Requirements',
        href: requirementsPath,
        icon: ClipboardList,
        group: 'archives',
      },
      {
        name: 'Change Logs',
        href: changeLogsPath,
        icon: History,
        group: 'archives',
      },
    ];

    const projectOrg = (state?.organizations || []).find(
      (org) => normalizeOrganizationId(org.id) === normalizeOrganizationId(project?.organizationId)
    );
    const canOpenAccounting = canViewProjectAccountingUi(currentUser, project, projectOrg);
    const accountingEnabled = !!(project?.accountingModules?.sr || project?.accountingModules?.fcm);
    if (canOpenAccounting && accountingEnabled) {
      items.push({
        name: 'Accounting',
        href: accountingPath,
        icon: DollarSign,
        group: 'finances',
      });
    }

    if (canOpenSettings) {
      items.push({
        name: 'Settings',
        href: settingsPath,
        icon: Settings,
        group: 'archives',
      });
    }

    return items;
  }, [
    calendarPath,
    canOpenSettings,
    changeLogsPath,
    epicsPath,
    ganttPath,
    meetingsPath,
    newsFeedPath,
    overviewPath,
    planningBoardPath,
    currentUser,
    project,
    project?.projectType,
    projectId,
    requirementsPath,
    settingsPath,
    accountingPath,
    sprintPlanningPath,
    spreadsheetPath,
    state?.organizations,
    sprintsPath,
    standupPath,
    tasksPath,
  ]);

  const projectNavigation = useMemo(() => {
    if (isExternalPartner) {
      return fullProjectNavigation.filter((item) => item.href === tasksPath || item.href === spreadsheetPath || item.href === costsPath);
    }

    return fullProjectNavigation;
  }, [fullProjectNavigation, isExternalPartner, spreadsheetPath, tasksPath, costsPath]);

  const projectNavigationOrder = useMemo(
    () => [
      // Top-level (flat)
      overviewPath,
      tasksPath,
      spreadsheetPath,
      calendarPath,
      standupPath,
      meetingsPath,
      // Planning group
      planningBoardPath,
      sprintsPath,
      sprintPlanningPath,
      ganttPath,
      costsPath,
      // Finances group
      accountingPath,
      // Archives group
      newsFeedPath,
      epicsPath,
      requirementsPath,
      changeLogsPath,
      settingsPath,
    ],
    [
      calendarPath,
      changeLogsPath,
      costsPath,
      epicsPath,
      ganttPath,
      meetingsPath,
      accountingPath,
      newsFeedPath,
      overviewPath,
      planningBoardPath,
      requirementsPath,
      settingsPath,
      sprintPlanningPath,
      spreadsheetPath,
      sprintsPath,
      standupPath,
      tasksPath,
    ]
  );

  const orderedProjectNavigation = useMemo(() => {
    const orderByHref = new Map(projectNavigationOrder.map((href, index) => [href, index]));

    return [...projectNavigation].sort((a, b) => {
      const aIndex = orderByHref.get(a.href);
      const bIndex = orderByHref.get(b.href);

      if (aIndex === undefined && bIndex === undefined) {
        return 0;
      }

      if (aIndex === undefined) {
        return 1;
      }

      if (bIndex === undefined) {
        return -1;
      }

      return aIndex - bIndex;
    });
  }, [projectNavigation, projectNavigationOrder]);

  const isNavigationItemActive = (item: ProjectNavigationItem) => {
    if (item.match) {
      return item.match(location.pathname);
    }

    return location.pathname === item.href;
  };

  const isSpreadsheetLayout = location.pathname === spreadsheetPath;

  const mobileBottomNavigation = useMemo(() => {
    if (isExternalPartner) {
      return [
        { name: 'Tasks', href: tasksPath, icon: ListTree },
        { name: 'Sheet', href: spreadsheetPath, icon: Grid3x3 },
        { name: 'Costs', href: costsPath, icon: DollarSign },
        { name: 'Hours', href: '/app/timesheet', icon: Clock },
        { name: 'Profile', href: '/app/profile', icon: UserCircle2 },
        { name: 'Home', href: '/app', icon: Home },
      ];
    }

    return [
      { name: 'Overview', href: overviewPath, icon: FileText },
      { name: 'Tasks', href: tasksPath, icon: ListTree },
      { name: 'Costs', href: costsPath, icon: DollarSign },
      { name: 'Hours', href: '/app/timesheet', icon: Clock },
      { name: 'Home', href: '/app', icon: Home },
    ];
  }, [isExternalPartner, projectId, spreadsheetPath, tasksPath]);

  const isMobileBottomNavActive = (path: string) => {
    if (path === tasksPath) {
      return location.pathname === tasksPath || location.pathname.startsWith(`/app/projects/${projectId}/task/`);
    }

    return location.pathname === path;
  };

  const projectSummary = useMemo(() => {
    const projectTasks = (state?.tasks ?? []).filter(
      (task) => String(task.projectId).toLowerCase() === String(projectId).toLowerCase()
    );

    const activeCount = projectTasks.filter(
      (task) => !['Completed', 'Deferred', 'Not Required'].includes(task.status)
    ).length;

    const blockedCount = projectTasks.filter((task) => task.status === 'Blocked').length;

    const now = new Date();
    const dueSoonBoundary = new Date(now);
    dueSoonBoundary.setDate(dueSoonBoundary.getDate() + 3);

    const dueSoonCount = projectTasks.filter((task) => {
      if (['Completed', 'Deferred', 'Not Required'].includes(task.status)) {
        return false;
      }

      const dueDate = parseDateLike(
        task.targetCompletionDate || task.targetEndDate || task.nextUpdateDate
      );

      if (!dueDate) {
        return false;
      }

      const dueTime = dueDate.getTime();
      return dueTime >= now.getTime() && dueTime <= dueSoonBoundary.getTime();
    }).length;

    return {
      activeCount,
      blockedCount,
      dueSoonCount,
    };
  }, [projectId, state?.tasks]);

  useEffect(() => {
    setMobileSearchOpen(false);
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [location.pathname]);

  const getUserInitials = (nameOrEmail?: string) => {
    if (!nameOrEmail) {
      return 'U';
    }

    const parts = nameOrEmail.split(' ').filter(Boolean);
    if (parts.length > 1) {
      return parts
        .map((part) => part[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
    }

    return nameOrEmail.slice(0, 2).toUpperCase();
  };

  if (!project) {
    return (
      <div className="app-shell flex items-center justify-center">
        <div className="text-slate-500 dark:text-slate-400">Loading project...</div>
      </div>
    );
  }

  if (!ndaAccepted) {
    return <ProjectNdaGate project={project} organizationName={organizationName} />;
  }

  if (isExternalPartner && location.pathname !== tasksPath && location.pathname !== spreadsheetPath) {
    return <Navigate to={tasksPath} replace />;
  }

  return (
    <div className="app-shell">
      {/* Two-row sticky project header */}
      <div className="project-shell-header sticky top-0 z-40 backdrop-blur">

            {/* Row 1: logo | breadcrumb | role | settings | avatar */}
            <div className="container mx-auto px-4">
              <div className="h-14 flex items-center gap-2.5">

                {/* Diligent logo */}
                <DiligentSlimBanner className="shrink-0" />

                <div className="h-5 w-px shrink-0 bg-slate-300 dark:bg-slate-600" />

                {/* Breadcrumb: Projects › Org › Project */}
                <div className="flex items-center gap-1 min-w-0 flex-1 text-sm">
                  <Link
                    to="/app/projects"
                    title="Go to all projects"
                    className="inline-flex shrink-0 items-center gap-1 rounded px-1 py-0.5 text-blue-700 transition-colors hover:bg-blue-50 hover:text-blue-700 dark:text-blue-300 dark:hover:bg-blue-950/40 dark:hover:text-blue-300"
                  >
                    <span className="font-medium">Projects</span>
                    <ExternalLink className="w-3 h-3" />
                  </Link>
                  <ChevronRight className="w-3 h-3 shrink-0 text-slate-500 dark:text-slate-400" />
                  <span className="hidden max-w-[110px] shrink-0 truncate text-slate-500 dark:text-slate-400 sm:block">
                    {organizationName}
                  </span>
                  <ChevronRight className="hidden w-3 h-3 shrink-0 text-slate-500 dark:text-slate-400 sm:block" />
                  <span className="truncate font-semibold text-slate-700 dark:text-slate-200">{project.name}</span>
                </div>

                {/* Role + settings + avatar */}
                <div className="flex items-center gap-1.5 shrink-0">
                  <span className="hidden rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] text-slate-600 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300 md:inline">
                    {displayRole}
                  </span>
                  <span className="hidden rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] text-slate-600 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300 xl:inline-flex">
                    {projectSummary.activeCount} active • {projectSummary.blockedCount} blocked • {projectSummary.dueSoonCount} due in 3d
                  </span>

                  {state?.currentUser && (
                    <Link
                      to="/app"
                      className="rounded-lg p-1.5 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-200 md:hidden"
                      title="Home"
                      aria-label="Go to dashboard home"
                    >
                      <Home className="w-4 h-4" />
                    </Link>
                  )}

                  {state?.currentUser && (
                    <button
                      onClick={() => setMobileSearchOpen((prev) => !prev)}
                      className="rounded-lg p-1.5 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-200 md:hidden"
                      title="Search"
                      aria-label="Toggle system search"
                    >
                      <Search className="w-4 h-4" />
                    </button>
                  )}

                  <Link
                    to="/app/profile"
                    className="rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-300 dark:focus-visible:ring-blue-700"
                  >
                    <Avatar className="h-8 w-8 border border-slate-200 dark:border-slate-700">
                      <AvatarImage src={currentUser?.avatar} alt={currentUser?.name || currentUser?.email || 'User'} />
                      <AvatarFallback className="bg-slate-100 text-[11px] text-slate-500 dark:bg-slate-800 dark:text-slate-300">
                        {getUserInitials(currentUser?.name || currentUser?.email)}
                      </AvatarFallback>
                    </Avatar>
                  </Link>
                </div>
              </div>
            </div>

            {/* Row 2: project navigation - two-level (flat top + collapsible groups) */}
            <div
              className="border-t border-slate-200 dark:border-slate-700 relative"
              ref={navGroupRef}
            >
              <div className="project-shell-groupbar border-b border-slate-200/70 dark:border-slate-700/70">
                <div className="container mx-auto px-4">
                  <div className="flex w-full min-w-0 items-center gap-0 overflow-x-auto">
                    {/* Flat top-level items */}
                    {orderedProjectNavigation.filter(i => i.group === 'top').map((item) => {
                      const Icon = item.icon;
                      const active = isNavigationItemActive(item);
                      return (
                        <div key={item.href} className="flex shrink-0 items-center">
                          <Link
                            to={item.href}
                            title={item.name}
                            aria-label={item.name}
                            className={`relative flex items-center gap-1 whitespace-nowrap border-b-2 -mb-px px-2.5 py-2 text-[11px] font-medium leading-none transition-colors group/navitem ${
                              active
                                ? 'border-blue-500 text-blue-700 dark:border-blue-400 dark:text-blue-300'
                                : 'border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-700 dark:text-slate-400 dark:hover:border-slate-600 dark:hover:text-slate-200'
                            }`}
                          >
                            <Icon className="h-3.5 w-3.5" />
                            <span className="hidden xl:inline">{item.name}</span>
                            {/* Tooltip for when text is hidden */}
                            <span className="xl:hidden pointer-events-none absolute left-1/2 top-full mt-1 -translate-x-1/2 rounded bg-slate-800 px-2 py-1 text-[10px] text-white opacity-0 group-hover/navitem:opacity-100 whitespace-nowrap z-50 transition-opacity">
                              {item.name}
                            </span>
                          </Link>
                        </div>
                      );
                    })}

                    {/* Group dropdown buttons */}
                    <div className="ml-auto flex items-center">
                      {NAV_GROUP_DEFS.map((groupDef) => {
                        const groupItems = orderedProjectNavigation.filter(i => i.group === groupDef.id);
                        if (groupItems.length === 0) return null;
                        const GroupIcon = groupDef.icon;
                        const isOpen = openNavGroup === groupDef.id;
                        const hasActive = groupItems.some(isNavigationItemActive);
                        return (
                          <div key={groupDef.id} className="flex shrink-0 items-center">
                            <button
                              type="button"
                              onClick={() => setOpenNavGroup(isOpen ? null : groupDef.id)}
                              className={`flex items-center gap-1 whitespace-nowrap border-b-2 -mb-px px-2.5 py-2 text-[11px] font-medium leading-none transition-colors ${
                                hasActive || isOpen
                                  ? 'border-blue-500 text-blue-700 dark:border-blue-400 dark:text-blue-300'
                                  : 'border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-700 dark:text-slate-400 dark:hover:border-slate-600 dark:hover:text-slate-200'
                              }`}
                            >
                              <GroupIcon className="h-3.5 w-3.5" />
                              <span className="hidden sm:inline">{groupDef.label}</span>
                              <ChevronDown className={`h-3 w-3 transition-transform duration-150 ${isOpen ? 'rotate-180' : ''}`} />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>

              {/* Dropdown panel for open group */}
              {openNavGroup && (() => {
                const activeGroup = NAV_GROUP_DEFS.find(g => g.id === openNavGroup);
                if (!activeGroup) return null;
                const groupItems = orderedProjectNavigation.filter(i => i.group === openNavGroup);
                if (groupItems.length === 0) return null;
                return (
                  <div
                    className="absolute left-0 right-0 top-full z-50 border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 shadow-lg"
                  >
                    <div className="container mx-auto px-4">
                      <div className="flex flex-wrap justify-start gap-1 py-2">
                        {groupItems.map((item) => {
                          const Icon = item.icon;
                          const active = isNavigationItemActive(item);
                          return (
                            <Link
                              key={item.href}
                              to={item.href}
                              title={item.name}
                              onClick={() => setOpenNavGroup(null)}
                              className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                                active
                                  ? 'bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300'
                                  : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-slate-100'
                              }`}
                            >
                              <Icon className="h-4 w-4" />
                              {item.name}
                            </Link>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>

            {mobileSearchOpen && state?.currentUser && (
              <div className="border-t border-slate-200 px-3 py-2 dark:border-slate-700 md:hidden">
                <SystemSearch
                  projects={state.projects || []}
                  tasks={state.tasks || []}
                  users={state.users || []}
                  currentUser={state.currentUser}
                />
              </div>
            )}
          </div>

      {/* Main Content */}
      <main className={isSpreadsheetLayout ? '' : 'pb-14 md:pb-0'}>
        <ProfileCompletionPrompt user={state?.currentUser ?? null} />
        <Outlet />
      </main>

      {/* Mobile bottom navigation bar */}
      {!isSpreadsheetLayout && (
        <nav className="app-shell-bar fixed bottom-0 left-0 right-0 z-40 border-t md:hidden">
          <div className="grid grid-cols-5 h-14">
            {mobileBottomNavigation.map(({ name, href, icon: Icon }) => {
              const active = isMobileBottomNavActive(href);

              return (
                <Link
                  key={href}
                  to={href}
                  className={`flex flex-col items-center justify-center gap-0.5 text-[11px] font-medium transition-colors ${
                    active ? 'text-blue-600 dark:text-blue-300' : 'text-slate-500 dark:text-slate-400'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span>{name}</span>
                </Link>
              );
            })}
          </div>
        </nav>
      )}
    </div>
  );
}